import { Alert } from '@lifebit-ai/lds'
import { ResultsState } from 'enums'
import React from 'react'

import { ResultFailureAlert } from 'modules/resultsV2/ResultFailureAlert'
import { AlertContainer } from 'modules/resultsV2/Results.styles'
import type { Result } from 'services/results/types'

import useResultAlerts from './hooks/useResultAlerts'
import {
  APPROVAL_ALERT_TEST_ID,
  AUTOMATIC_APPROVAL_ALERT_TEST_ID,
  RE_REQUEST_BY_DIFFERENT_USER_ALERT_TEST_ID,
  RE_REQUEST_WITH_UPDATED_FILE_ALERT_TEST_ID
} from './utils'

interface ResultAlertsProps {
  isApprovalRestricted: boolean
  result: Result | undefined
  onClickRetry: (e: React.MouseEvent<HTMLButtonElement>) => void
  isRetrying: boolean
  isRetryingDisabled: boolean
}

const ResultAlerts = ({
  isApprovalRestricted,
  result,
  onClickRetry,
  isRetrying,
  isRetryingDisabled
}: ResultAlertsProps): JSX.Element | null => {
  const { showReRequestWithUpdatedFileAlert, showReRequestByDifferentUserAlert, showReRequestAutoApprovedAlert } =
    useResultAlerts({ result })

  if (!result) return null

  return (
    <>
      {isApprovalRestricted && (
        <AlertContainer>
          <Alert type="info" data-testid={APPROVAL_ALERT_TEST_ID}>
            This export request is from a workspace with approval restrictions enabled. Since you submitted the request,
            you cannot preview, download, approve, or reject it. Another Airlock Manager will need to review it.
          </Alert>
        </AlertContainer>
      )}
      {[ResultsState.Rejected, ResultsState.Failed].includes(result.status) && (
        <AlertContainer>
          <ResultFailureAlert
            result={result}
            onClickRetry={onClickRetry}
            isRetrying={isRetrying}
            isRetryingDisabled={isRetryingDisabled}
          />
        </AlertContainer>
      )}
      {showReRequestByDifferentUserAlert ? (
        <AlertContainer>
          <Alert type="warning" data-testid={RE_REQUEST_BY_DIFFERENT_USER_ALERT_TEST_ID} showIcon>
            This is a re-request — manual review is required. This request has been submitted by a different user to the
            one on the original approved request. Manual review is required regardless of file status. Please review
            carefully before approving or rejecting.
          </Alert>
        </AlertContainer>
      ) : showReRequestWithUpdatedFileAlert ? (
        <AlertContainer>
          <Alert type="warning" data-testid={RE_REQUEST_WITH_UPDATED_FILE_ALERT_TEST_ID} showIcon>
            This is a re-request — the file content has changed since the original approval. The file no longer matches
            the previously approved version. Please review carefully before approving or rejecting.
          </Alert>
        </AlertContainer>
      ) : showReRequestAutoApprovedAlert ? (
        <AlertContainer>
          <Alert type="info" data-testid={AUTOMATIC_APPROVAL_ALERT_TEST_ID} showIcon>
            This request was automatically approved — no action required. The file was verified as identical to the
            previously approved version.
          </Alert>
        </AlertContainer>
      ) : null}
    </>
  )
}

export default ResultAlerts
